import { motion } from 'framer-motion'
import { useReferrals } from '../../../state/referrals'
import { useAuth } from '../../../state/auth'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { ChevronDown, ChevronRight, Users, TrendingUp, Share2, LinkIcon, Percent, ArrowLeft, Network, LayoutList, Search, UserCheck } from 'lucide-react'
import { useState, useMemo } from 'react'
import type { HierarchicalReferral } from '../../../models/referral'
import { CopyButton } from '@/components/ui/shadcn-io/copy-button'
import { useTranslation } from '../../../hooks/useTranslation'
import useSystemDefaults from '../../../hooks/queries/useSystemDefaults'
import { useDesignSystem } from '@/hooks/useDesignSystem'
import { LoadingSpinner } from '@/components/ui/loading'
import { OrbitalTree } from '@/components/referrals/orbital-tree'
import { Input } from '@/components/ui/input'

interface NavigationState {
  currentUser: HierarchicalReferral | null
  parentStack: HierarchicalReferral[]
}

// Helper function to count referrals by level (only levels 1-3)
function countReferralsByLevel(referrals: HierarchicalReferral[]): { [key: number]: number } {
  const counts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0 }

  function traverse(refs: HierarchicalReferral[]) {
    refs.forEach(ref => {
      if (ref.level >= 1 && ref.level <= 3) {
        counts[ref.level] = (counts[ref.level] || 0) + 1
      }
      if (ref.children && ref.children.length > 0) {
        traverse(ref.children)
      }
    })
  }

  traverse(referrals)
  return counts
}

// Helper function to count active referrals (those with subscriptions)
function countActiveReferrals(referrals: HierarchicalReferral[]): number {
  let count = 0

  function traverse(refs: HierarchicalReferral[]) {
    refs.forEach(ref => {
      if (ref.subscriptions && ref.subscriptions.length > 0) {
        count++
      }
      if (ref.children && ref.children.length > 0) {
        traverse(ref.children)
      }
    })
  }

  traverse(referrals)
  return count
}



// Helper function to filter referrals by search term
function filterReferrals(referrals: HierarchicalReferral[], searchTerm: string): HierarchicalReferral[] {
  if (!searchTerm.trim()) return referrals

  const term = searchTerm.toLowerCase()

  function matches(ref: HierarchicalReferral): boolean {
    return ref.user.toLowerCase().includes(term)
  }

  function filterRecursive(refs: HierarchicalReferral[]): HierarchicalReferral[] {
    return refs.reduce((acc: HierarchicalReferral[], ref) => {
      const filteredChildren = ref.children ? filterRecursive(ref.children) : []

      if (matches(ref) || filteredChildren.length > 0) {
        acc.push({
          ...ref,
          children: filteredChildren
        })
      }

      return acc
    }, [])
  }

  return filterRecursive(referrals)
}

function ReferralTreeNode({ referral, level = 0 }: { referral: HierarchicalReferral; level?: number }) {
  const [expanded, setExpanded] = useState(false)
  const hasChildren = referral.children && referral.children.length > 0
  const hasSubscriptions = referral.subscriptions && referral.subscriptions.length >= 1
  const { patterns } = useDesignSystem()

  return (
    <motion.div
      variants={level === 0 ? undefined : undefined}
      initial={level === 0 ? undefined : { opacity: 1, x: 0 }}
      style={{ marginLeft: level * 24 }}
    >
      <div className="flex items-center space-x-2 py-3 px-3 rounded-lg hover:bg-muted/50 transition-colors">
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 hover:bg-muted rounded transition-colors"
          disabled={!hasChildren}
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>

        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center text-white text-sm font-medium', patterns.radius.full, 'bg-muted')}>
              {referral.user?.[0]?.toUpperCase() || 'U'}
            </div>

            {/* Subscription Amount Badges */}
            <div
              className={patterns.utils.cx('w-2 h-2 rounded-full',
                hasSubscriptions ? 'bg-green-500' : 'bg-red-500'
              )}
              title={hasSubscriptions ? 'Active subscription' : 'No active subscription'}
            />
            <div className="flex items-center gap-2">
              <span className={patterns.text.heading}>{referral.user}</span>

              {/* Status Dot */}

              <div className={'text-xs ' + patterns.text.muted}>
                Level {referral.level} • {new Date(referral.created_at).toLocaleDateString()}
              </div>
            </div>

            {referral.subscriptions && referral.subscriptions.length > 0 && (
              <div className="flex items-center gap-1">
                {referral.subscriptions.map((sub, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-green-500/20 text-green-400 border-green-500/30 text-xs"
                  >
                    ${sub.amount}
                  </Badge>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {expanded && hasChildren && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="ml-4 border-l-2 border-border overflow-hidden"
        >
          {referral.children?.map((child: HierarchicalReferral, index: number) => (
            <ReferralTreeNode key={`${child.user}-${index}`} referral={child} level={level + 1} />
          ))}
        </motion.div>
      )}
    </motion.div>
  )
}

export default function ReferralsPage() {
  const { myReferrals, referralSummary, dataIsLoading, dataError, refetch } = useReferrals()
  const { user } = useAuth()
  const { t } = useTranslation()
  const { systemDefaults, isLoading: systemDefaultsLoading } = useSystemDefaults()
  const { animations, patterns } = useDesignSystem()
  const [activeTab, setActiveTab] = useState<'list' | 'tree'>('list')
  const [searchTerm, setSearchTerm] = useState('')
  const [navigation, setNavigation] = useState<NavigationState>({
    currentUser: null,
    parentStack: []
  })

  // Calculate KPIs
  const levelCounts = useMemo(() => countReferralsByLevel(myReferrals || []), [myReferrals])
  const activeReferralsCount = useMemo(() => countActiveReferrals(myReferrals || []), [myReferrals])

  // Filter referrals based on search term
  const filteredReferrals = useMemo(() => {
    return filterReferrals(myReferrals || [], searchTerm)
  }, [myReferrals, searchTerm])

  const handleNodeClick = (referral: HierarchicalReferral) => {
    if (navigation.currentUser) {
      setNavigation(prev => ({
        currentUser: referral,
        parentStack: [...prev.parentStack, prev.currentUser!]
      }))
    } else {
      setNavigation({
        currentUser: referral,
        parentStack: []
      })
    }
  }

  const handleBack = () => {
    setNavigation(prev => {
      if (prev.parentStack.length === 0) {
        return { currentUser: null, parentStack: [] }
      }
      const newStack = [...prev.parentStack]
      const parent = newStack.pop()
      return {
        currentUser: parent || null,
        parentStack: newStack
      }
    })
  }

  const getCurrentReferrals = (): HierarchicalReferral[] => {
    if (navigation.currentUser) {
      return navigation.currentUser.children || []
    }
    return myReferrals || []
  }

  const getCurrentUserName = (): string => {
    if (navigation.currentUser) {
      return navigation.currentUser.user
    }
    return user?.name || user?.username || 'You'
  }

  if (dataIsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <LoadingSpinner size="lg" text={t('pages.referrals.loadingReferrals')} />
      </div>
    )
  }

  if (dataError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className={'text-lg font-semibold mb-2 ' + patterns.text.heading}>
            {t('pages.referrals.loadReferralsError')}
          </h3>
          <p className={patterns.text.muted + ' mb-4'}>
            {t('pages.referrals.loadReferralsMessage')}
          </p>
          <Button
            onClick={() => refetch()}
            variant="outline"
            className="bg-white hover:bg-muted dark:bg-card dark:hover:bg-muted"
          >
            {t('pages.referrals.tryAgain')}
          </Button>
        </div>
      </div>
    )
  }

  const stats = referralSummary || {
    direct_referrals: 0,
    total_referrals: 0,
    total_commission_earned: 0,
    commission_by_level: []
  }

  // Get commission rates from system defaults
  const getCommissionRate = (level: string) => {
    const commissionDefault = systemDefaults?.find(def => def.name === level)
    return commissionDefault ? `${commissionDefault.value}%` : 'Loading...'
  }

  const currentReferrals = getCurrentReferrals()
  const currentUserName = getCurrentUserName()
  const canGoBack = navigation.currentUser !== null

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={animations.component.staggerContainer}
      className="space-y-8"
    >
      {/* Header Section */}
      <motion.div variants={animations.content.header} className="text-center space-y-4">
        <h1 className={'text-4xl lg:text-5xl font-bold ' + patterns.text.heading}>
          {t('pages.referrals.title')}
        </h1>
        <p className={'text-lg max-w-2xl mx-auto ' + patterns.text.muted}>
          {t('pages.referrals.subtitle')}
        </p>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        variants={animations.component.staggerContainer}
        initial="hidden"
        animate="visible"
        className={patterns.layout.cardGrid}
      >
        <motion.div variants={animations.stats.statsCard} whileHover={{ y: -4, transition: { duration: 0.15 } }}>
          <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('pages.referrals.totalReferrals')}</CardTitle>
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <Users className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_referrals}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pages.referrals.totalReferrals')}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={animations.stats.statsCard} whileHover={{ y: -4, transition: { duration: 0.15 } }}>
          <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('pages.referrals.totalEarnings')}</CardTitle>
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <TrendingUp className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${stats.total_commission_earned}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pages.referrals.commissionEarned')}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={animations.stats.statsCard} whileHover={{ y: -4, transition: { duration: 0.15 } }}>
          <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('pages.referrals.referralCode')}</CardTitle>
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <LinkIcon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-mono flex flex-row font-bold tracking-wider">
                <div className={patterns.utils.cx('flex-row flex justify-between items-center py-0.5 ps-4 bg-muted', patterns.radius.full)} >
                  {user?.ref_code || 'Loading...'}
                  <CopyButton content={user?.ref_code} className='bg-muted-foreground/20 rounded-full' />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pages.referrals.yourUniqueCode')}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={animations.stats.statsCard} whileHover={{ y: -4, transition: { duration: 0.15 } }}>
          <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('pages.referrals.referralLink')}</CardTitle>
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <Share2 className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-mono flex flex-row font-bold tracking-wider">
                <div className={patterns.utils.cx('flex-row flex justify-between items-center py-0.5 ps-4 bg-muted', patterns.radius.full)} >
                  <span className="truncate max-w-[120px]">
                    https://nexa-academy.com/ref?code={user?.ref_code}
                  </span>
                  <CopyButton
                    content={`https://nexa-academy.com/ref?code=${user?.ref_code}`}
                    className='bg-muted-foreground/20 rounded-full'
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pages.referrals.shareThisLink')}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Detailed KPIs Card */}
      <motion.div variants={animations.stats.statsCard}>
        <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-muted-foreground">
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <UserCheck className="h-4 w-4" />
              </div>
              {t('pages.referrals.referralBreakdown')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Direct Referrals */}
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-white mb-1">
                  {stats.direct_referrals}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {t('pages.referrals.directReferrals')}
                </div>
              </div>

              {/* Level 1 */}
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-blue-400 mb-1">
                  {levelCounts[1]}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {t('pages.referrals.level1Referrals')}
                </div>
              </div>

              {/* Level 2 */}
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-green-400 mb-1">
                  {levelCounts[2]}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {t('pages.referrals.level2Referrals')}
                </div>
              </div>

              {/* Level 3 */}
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400 mb-1">
                  {levelCounts[3]}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {t('pages.referrals.level3Referrals')}
                </div>
              </div>

              {/* In-active Referrals */}
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-purple-400 mb-1">
                  {stats.total_referrals - activeReferralsCount}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {t('pages.referrals.inactiveReferrals')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Commission Rates Card */}
      <motion.div variants={animations.stats.statsCard}>
        <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-muted-foreground">
              <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
                <Percent className="h-4 w-4" />
              </div>
              {t('pages.referrals.commissionRates')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['commission_l1', 'commission_l2', 'commission_l3'].map((level, index) => (
                <div key={level} className="text-center">
                  <div className="text-2xl font-bold text-white mb-1">
                    {systemDefaultsLoading ? '...' : getCommissionRate(level)}
                  </div>
                  <div className="text-xs text-muted-foreground font-medium">
                    {t('pages.referrals.level', { number: index + 1 })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                {t('pages.referrals.commissionRatesDescription')}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Referral Tree */}
      <motion.div variants={animations.stats.statsCard}>
        <Card className={patterns.card.base}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                {t('pages.referrals.referralNetwork')}
              </CardTitle>

              <div className="flex items-center gap-3">
                {/* Search Box */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={t('pages.referrals.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-[200px] md:w-[280px] bg-muted border-0 focus-visible:ring-primary"
                  />
                </div>

                {/* Tab Switcher */}
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab('list')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'list'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    <LayoutList className="h-4 w-4" />
                    List
                  </button>
                  <button
                    onClick={() => setActiveTab('tree')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'tree'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    <Network className="h-4 w-4" />
                    Tree
                  </button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activeTab === 'list' ? (
              // List View
              filteredReferrals && filteredReferrals.length > 0 ? (
                <div className="space-y-4">
                  <div className={patterns.utils.cx('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6')}>
                    <div className={patterns.utils.cx('p-4 border border-border bg-muted/50', patterns.radius.xl)}>
                      <div className={'text-sm font-medium mb-1 ' + patterns.text.muted}>{t('pages.referrals.directReferrals')}</div>
                      <div className={'text-2xl font-bold ' + patterns.text.heading}>{stats.direct_referrals}</div>
                    </div>
                    <div className={patterns.utils.cx('p-4 border border-border bg-muted/50', patterns.radius.xl)}>
                      <div className={'text-sm font-medium mb-1 ' + patterns.text.muted}>{t('pages.referrals.totalNetwork')}</div>
                      <div className={'text-2xl font-bold ' + patterns.text.heading}>{stats.total_referrals}</div>
                    </div>
                    <div className={patterns.utils.cx('p-4 border border-border bg-muted/50', patterns.radius.xl)}>
                      <div className={'text-sm font-medium mb-1 ' + patterns.text.muted}>{t('pages.referrals.commissionEarned')}</div>
                      <div className={'text-2xl font-bold ' + patterns.text.heading}>${stats.total_commission_earned}</div>
                    </div>
                  </div>
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={animations.component.staggerContainer}
                    className="space-y-1"
                  >
                    {filteredReferrals.map((referral: HierarchicalReferral, index: number) => (
                      <ReferralTreeNode key={`${referral.user}-${index}`} referral={referral} />
                    ))}
                  </motion.div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className={'text-lg font-semibold mb-2 ' + patterns.text.heading}>
                    {searchTerm ? t('pages.referrals.noSearchResults') : t('pages.referrals.noReferrals')}
                  </h3>
                  <p className={patterns.text.muted + ' mb-4'}>
                    {searchTerm ? t('pages.referrals.tryDifferentSearch') : t('pages.referrals.shareMessage')}
                  </p>
                  {!searchTerm && (
                    <Button
                      onClick={() => navigator.clipboard.writeText(user?.ref_code || '')}
                      disabled={!user?.ref_code}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      {t('pages.referrals.shareCode')}
                    </Button>
                  )}
                </div>
              )
            ) : (
              // Tree View
              <div className="relative">
                {/* Back Button */}
                {canGoBack && (
                  <button
                    onClick={handleBack}
                    className="absolute top-0 left-0 z-10 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                )}

                {/* Current User Indicator */}
                {canGoBack && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
                        {currentUserName.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium">{currentUserName}</span>
                    </div>
                  </div>
                )}

                {currentReferrals.length > 0 ? (
                  <OrbitalTree
                    referrals={currentReferrals}
                    currentUserName={currentUserName}
                    onNodeClick={handleNodeClick}
                    currentCenterId={navigation.currentUser?.user}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                      <Users className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className={'text-lg font-semibold ' + patterns.text.heading}>
                      No referrals yet
                    </h3>
                    <p className={patterns.text.muted}>
                      This user hasn't referred anyone yet
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  )
}
